import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { 
  ArrowLeft, Calendar, 
  Shield, Users,
  Wallet, Music, ChevronLeft, ChevronRight,
  Flame
} from "lucide-react";
import { formatDate } from "../utils/dateFormat";
import { generateCentralisedPDF, generateKjAllowancePDF } from "../utils/adminPdfGenerators";
import LanguageSelector from "../components/LanguageSelector";
// Admin components (refactored)
import { 
  OnePADialog, 
  AssignKJDialog,
  EditMemberModal,
  AdminKaraokeTab,
  AdminBBQTab,
  AdminCalendarTab,
  AdminDirectoryTab,
  AdminFinanceTab,
  AdminKJOpsTab,
} from "../components/admin";

const AdminPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  
  // Main state
  const [activeTab, setActiveTab] = useState('karaoke');
  const [activeSubView, setActiveSubView] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [confirmedBookings, setConfirmedBookings] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  
  // Server-side pagination state for Karaoke bookings
  const [bookingsPagination, setBookingsPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [bookingsSearch, setBookingsSearch] = useState("");
  const [bookingsKjFilter, setBookingsKjFilter] = useState("");
  const [bookingsStatusFilter, setBookingsStatusFilter] = useState("all");
  const [bookingsSortBy, setBookingsSortBy] = useState("date_desc");
  const [bookingsLoading, setBookingsLoading] = useState(false);
  
  // BBQ State
  const [bbqBookings, setBbqBookings] = useState([]);
  const [bbqPendingBookings, setBbqPendingBookings] = useState([]);
  const [bbqSearchQuery, setBbqSearchQuery] = useState("");
  
  // Annual Summary State
  const [annualSummary, setAnnualSummary] = useState(null);
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [loadingAnnual, setLoadingAnnual] = useState(false);
  
  // Centralised Report State
  const [centralisedReport, setCentralisedReport] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Form states
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [showBlackoutForm, setShowBlackoutForm] = useState(false);
  const [showFetchApiForm, setShowFetchApiForm] = useState(false);
  const [showSpecialBookingForm, setShowSpecialBookingForm] = useState(false);
  const [fetchApiYear, setFetchApiYear] = useState(new Date().getFullYear() + 3);
  const [fetchingFromApi, setFetchingFromApi] = useState(false);
  const [holidayData, setHolidayData] = useState({ date: "", description: "" });
  const [blackoutData, setBlackoutData] = useState({ 
    start_date: "", 
    end_date: "", 
    reason: "",
    facilities: ["karaoke", "bbq"] // default to both
  });
  const [blackoutConflicts, setBlackoutConflicts] = useState([]);
  const [importingHolidays, setImportingHolidays] = useState(false);
  const [kjList, setKjList] = useState([]);
  const [kjDetails, setKjDetails] = useState([]); // KJ list with eligibility info
  const [kjReport, setKjReport] = useState(null);
  const [kjReportMonth, setKjReportMonth] = useState(new Date().getMonth() + 1);
  const [kjReportYear, setKjReportYear] = useState(new Date().getFullYear());
  const [loadingKjReport, setLoadingKjReport] = useState(false);
  const [specialBookingData, setSpecialBookingData] = useState({
    member_name: "",
    member_email: "",
    member_phone: "",
    date: "",
    slot_type: "afternoon",
    discount_type: "none",
    kj_required: false,
    kj_name: "",
    discount_note: ""
  });
  const [memberData, setMemberData] = useState({
    name: "",
    email: "",
    phone: "",
    mailing_address: "",
    password: "",
    privacy_consent: true,
    is_admin: false,
    is_kj: true,
    is_rn_member: false,
    kj_allowance_eligible: true
  });
  
  // onePA Transaction Verification State
  const [showOnePADialog, setShowOnePADialog] = useState(false);
  const [onePATransactionNumber, setOnePATransactionNumber] = useState("");
  const [pendingVerification, setPendingVerification] = useState(null); // {type: 'karaoke'|'bbq', bookingId: string}
  
  // KJ Assignment State
  const [showAssignKJDialog, setShowAssignKJDialog] = useState(false);
  const [bookingToAssignKJ, setBookingToAssignKJ] = useState(null);

  // Edit Member State
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberData, setEditMemberData] = useState({
    name: "",
    email: "",
    phone: "",
    mailing_address: "",
    is_admin: false,
    is_kj: false,
    kj_allowance_eligible: false,
  });

  // Tab definitions
  const tabs = [
    { id: 'karaoke', label: 'Karaoke', icon: Music },
    { id: 'bbq', label: 'BBQ Pits', icon: Flame, notranslate: true },
    { id: 'calendar', label: 'Calendar Mgmt', icon: Calendar },
    { id: 'directory', label: 'Directory', icon: Users },
    { id: 'finance', label: 'Finance', icon: Wallet },
    { id: 'kjops', label: 'KJ Ops', icon: Music },
  ];

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      // Fetch all data independently - one failure shouldn't block others
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/public-holidays`),
        axios.get(`${API}/admin/blackout-dates`),
        axios.get(`${API}/admin/finance/kj-list`),
        axios.get(`${API}/admin/bbq/bookings?page_size=100`)
      ]);
      
      const [holidaysRes, blackoutsRes, kjListRes, bbqRes] = results;
      
      if (holidaysRes.status === 'fulfilled') setHolidays(Array.isArray(holidaysRes.value.data) ? holidaysRes.value.data : []);
      if (blackoutsRes.status === 'fulfilled') setBlackoutDates(Array.isArray(blackoutsRes.value.data) ? blackoutsRes.value.data : []);
      if (kjListRes.status === 'fulfilled') {
        const kjData = kjListRes.value.data || {};
        setKjList(Array.isArray(kjData.kj_list) ? kjData.kj_list : []);
        setKjDetails(Array.isArray(kjData.kj_details) ? kjData.kj_details : []);
      }
      if (bbqRes.status === 'fulfilled') {
        const bbqData = bbqRes.value.data;
        const allBbqBookings = Array.isArray(bbqData?.bookings) ? bbqData.bookings : Array.isArray(bbqData) ? bbqData : [];
        setBbqBookings(allBbqBookings);
        setBbqPendingBookings(allBbqBookings.filter(b => b.status === 'pending'));
      }
      
      // Fetch pending bookings separately (needed for badge count)
      try {
        const pendingRes = await axios.get(`${API}/admin/bookings?page_size=100&status_filter=pending`);
        const pendingData = pendingRes.data;
        setPendingBookings(Array.isArray(pendingData?.bookings) ? pendingData.bookings : Array.isArray(pendingData) ? pendingData : []);
      } catch (e) { /* badge will show 0 */ }
      
      // Always fetch paginated bookings for the main table
      await fetchPaginatedBookings(1, bookingsPagination.pageSize, bookingsSearch, bookingsKjFilter, bookingsStatusFilter, bookingsSortBy);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch paginated bookings with server-side filtering
  const fetchPaginatedBookings = async (page, pageSize, search = "", kjFilter = "", statusFilter = "all", sortBy = "date_desc") => {
    setBookingsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (search.trim()) params.append('search', search.trim());
      if (kjFilter) params.append('kj_filter', kjFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (sortBy) params.append('sort_by', sortBy);
      
      const res = await axios.get(`${API}/admin/bookings?${params.toString()}`);
      const data = res.data;
      
      // Handle both paginated and non-paginated responses - ensure array
      const bookingsList = Array.isArray(data?.bookings) ? data.bookings : Array.isArray(data) ? data : [];
      setBookings(bookingsList);
      
      if (data.pagination) {
        setBookingsPagination({
          currentPage: data.pagination.current_page,
          pageSize: data.pagination.page_size,
          totalCount: data.pagination.total_count,
          totalPages: data.pagination.total_pages,
          hasNext: data.pagination.has_next,
          hasPrev: data.pagination.has_prev
        });
      }
      
      // Update confirmed bookings for other views (includes all active bookings for customer contacts)
      const confirmed = bookingsList
        .filter(b => b.status === 'pending' || b.status === 'paid' || b.status === 'completed')
        .sort((a, b) => {
          if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
          return a.slot_type === 'afternoon' ? -1 : 1;
        });
      setConfirmedBookings(confirmed);
    } catch (error) {
      toast.error("Failed to fetch bookings");
    } finally {
      setBookingsLoading(false);
    }
  };
  
  // Handle pagination page change
  const handleBookingsPageChange = (newPage) => {
    fetchPaginatedBookings(newPage, bookingsPagination.pageSize, bookingsSearch, bookingsKjFilter, bookingsStatusFilter, bookingsSortBy);
  };
  
  // Handle search change with debounce
  const handleBookingsSearchChange = (search) => {
    setBookingsSearch(search);
    fetchPaginatedBookings(1, bookingsPagination.pageSize, search, bookingsKjFilter, bookingsStatusFilter, bookingsSortBy);
  };
  
  // Handle KJ filter change
  const handleBookingsKjFilterChange = (filter) => {
    setBookingsKjFilter(filter);
    fetchPaginatedBookings(1, bookingsPagination.pageSize, bookingsSearch, filter, bookingsStatusFilter, bookingsSortBy);
  };

  // Handle status filter change
  const handleBookingsStatusFilterChange = (statusFilter) => {
    setBookingsStatusFilter(statusFilter);
    fetchPaginatedBookings(1, bookingsPagination.pageSize, bookingsSearch, bookingsKjFilter, statusFilter, bookingsSortBy);
  };

  // Handle sort change
  const handleBookingsSortChange = (sortBy) => {
    setBookingsSortBy(sortBy);
    fetchPaginatedBookings(1, bookingsPagination.pageSize, bookingsSearch, bookingsKjFilter, bookingsStatusFilter, sortBy);
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/admin/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error("Failed to fetch members");
    }
  };

  // Handlers
  const handleVerifyPayment = async (bookingOrId) => {
    // Accept both a booking object or a booking_id string
    const id = typeof bookingOrId === 'string' ? bookingOrId : bookingOrId?.booking_id;
    setPendingVerification({ type: 'karaoke', bookingId: id });
    setOnePATransactionNumber("");
    setShowOnePADialog(true);
  };
  
  // Actual verification after onePA number is entered
  const confirmVerifyPayment = async (paymentDate = null) => {
    if (!onePATransactionNumber.trim()) {
      toast.error("onePA Transaction Number is required");
      return;
    }
    
    try {
      let url;
      const params = new URLSearchParams({ onepa_transaction_number: onePATransactionNumber.trim() });
      if (paymentDate) {
        params.append('payment_date', new Date(paymentDate + 'T00:00:00').toISOString());
      }
      
      if (pendingVerification.type === 'karaoke') {
        url = `${API}/admin/bookings/${pendingVerification.bookingId}/verify-payment?${params.toString()}`;
      } else if (pendingVerification.type === 'bbq') {
        url = `${API}/admin/bbq/bookings/${pendingVerification.bookingId}/verify-payment?${params.toString()}`;
      }
      await axios.put(url);
      toast.success("Payment verified successfully");
      setShowOnePADialog(false);
      setOnePATransactionNumber("");
      setPendingVerification(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify payment");
    }
  };

  // Helper function to check if cancellation is allowed (30 min kill switch)
  const canCancelBooking = (booking) => {
    try {
      const bookingDate = new Date(booking.date);
      const slotType = booking.slot_type;
      
      // Set slot start time
      if (slotType === 'afternoon') {
        bookingDate.setHours(13, 0, 0, 0);
      } else {
        bookingDate.setHours(18, 0, 0, 0);
      }
      
      const now = new Date();
      const timeDiff = bookingDate.getTime() - now.getTime();
      
      // Kill switch: No cancellation within 30 minutes
      return timeDiff > (30 * 60 * 1000);
    } catch {
      return false;
    }
  };

  const handleCancelPendingBooking = async (booking) => {
    if (!canCancelBooking(booking)) {
      toast.error("Cannot cancel booking within 30 minutes of session start");
      return;
    }
    
    const confirmed = window.confirm(
      `Cancel pending booking ${booking.booking_code}?\n\n` +
      `Member: ${booking.member_name}\n` +
      `Date: ${booking.date}\n\n` +
      `This booking has not been paid - no refund required.`
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/bookings/${booking.booking_id}/cancel-pending`);
      toast.success("Pending booking cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const handleCancelPaidBooking = async (booking) => {
    if (!canCancelBooking(booking)) {
      toast.error("Cannot cancel booking within 30 minutes of session start. Kill switch activated.");
      return;
    }
    
    const confirmed = window.confirm(
      `CANCEL PAID BOOKING ${booking.booking_code}?\n\n` +
      `Member: ${booking.member_name}\n` +
      `Date: ${booking.date}\n` +
      `Amount: $${booking.price}\n\n` +
      `STRICTLY NO REFUND as per policy.\n\n` +
      `This will:\n` +
      `• Change status to 'Cancelled'\n` +
      `• Notify the member (no refund)\n` +
      `• Release the slot for other bookings\n\n` +
      `For date changes instead, use the "Change Date" option.\n\n` +
      `Proceed with cancellation (NO refund)?`
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/bookings/${booking.booking_id}/full-refund-cancel`);
      toast.success(`Booking cancelled. No refund (as per policy).`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const handleChangeBookingDate = async (booking) => {
    const newDate = prompt(
      `Change date for ${booking.booking_code}\n\nCurrent: ${booking.date} (${booking.slot_type})\n\nEnter new date (YYYY-MM-DD):`,
      booking.date
    );
    if (!newDate) return;
    
    const newSlot = prompt(
      `Select time slot for ${newDate}:\n\nEnter: afternoon OR evening`,
      booking.slot_type
    );
    if (!newSlot || !['afternoon', 'evening'].includes(newSlot)) {
      toast.error("Invalid slot type. Must be 'afternoon' or 'evening'");
      return;
    }
    
    const confirmed = window.confirm(
      `Change booking ${booking.booking_code} date?\n\n` +
      `FROM: ${booking.date} (${booking.slot_type})\n` +
      `TO: ${newDate} (${newSlot})\n\n` +
      `Member will be notified via email.`
    );
    if (!confirmed) return;
    
    try {
      await axios.put(`${API}/admin/bookings/${booking.booking_id}/change-date?new_date=${newDate}&new_slot_type=${newSlot}`);
      toast.success(`Booking date changed to ${newDate} (${newSlot})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change date");
    }
  };

  const handleAutoCompletePast = async () => {
    const confirmed = window.confirm(
      "This will mark ALL past paid bookings as completed and trigger KJ allowances where applicable.\n\nContinue?"
    );
    if (!confirmed) return;
    
    try {
      const result = await axios.post(`${API}/admin/bookings/auto-complete-past`);
      toast.success(`${result.data.completed_count} bookings marked as completed`);
      fetchData();
    } catch (error) {
      toast.error("Failed to auto-complete bookings");
    }
  };

  // KJ Assignment Handler
  const handleAssignKJ = (booking) => {
    setBookingToAssignKJ(booking);
    setShowAssignKJDialog(true);
  };
  
  const handleAssignKJSuccess = () => {
    // Refresh bookings after KJ assignment
    fetchData();
    setShowAssignKJDialog(false);
    setBookingToAssignKJ(null);
  };

  // Handle swap approve
  const handleApproveSwap = async (booking) => {
    if (!window.confirm(`Approve swap request for ${booking.booking_code}?\n\nThis will assign ${booking.swap_requested_kj_name} as the new KJ.`)) return;
    try {
      await axios.put(`${API}/admin/bookings/${booking.booking_id}/approve-swap`);
      toast.success(`Swap approved! ${booking.swap_requested_kj_name} is now assigned.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve swap");
    }
  };

  // Handle swap reject
  const handleRejectSwap = async (booking) => {
    if (!window.confirm(`Reject swap request for ${booking.booking_code}?\n\n${booking.swap_requested_by_name || booking.kj_name} will remain assigned.`)) return;
    try {
      await axios.put(`${API}/admin/bookings/${booking.booking_id}/reject-swap`);
      toast.success(`Swap rejected. ${booking.swap_requested_by_name || booking.kj_name} remains assigned.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject swap");
    }
  };

  const handleDeleteHoliday = async (holidayIdOrDate) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await axios.delete(`${API}/admin/public-holidays/${holidayIdOrDate}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success("Holiday deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete holiday");
    }
  };

  const handleDeleteBlackout = async (id) => {
    if (!window.confirm("Delete this blackout period?")) return;
    try {
      await axios.delete(`${API}/admin/blackout-dates/${id}`);
      toast.success("Blackout period deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete blackout");
    }
  };

  // Edit Member handlers
  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditMemberData({
      name: member.name || "",
      email: member.email || "",
      phone: member.phone || "",
      mailing_address: member.mailing_address || "",
      is_admin: member.is_admin || false,
      is_kj: member.is_kj || false,
      kj_allowance_eligible: member.kj_allowance_eligible || false,
    });
    setShowEditMemberModal(true);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/members/${editingMember.member_id}`, editMemberData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Member "${editMemberData.name}" updated successfully`);
      setShowEditMemberModal(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Delete member "${member.name}"?\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${member.member_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Member "${member.name}" deleted`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete member");
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/public-holidays`, holidayData);
      toast.success("Holiday added successfully");
      setHolidayData({ date: "", description: "" });
      setShowHolidayForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add holiday");
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();
    try {
      const result = await axios.post(`${API}/admin/blackout-dates`, blackoutData);
      const facilities = blackoutData.facilities;
      const scopeLabel = facilities.length === 2 ? "both facilities" : 
                        facilities.includes('karaoke') ? "Karaoke Booking" : "BBQ Pit Booking";
      
      if (result.data.conflicts && result.data.conflicts.length > 0) {
        toast.success(`Blackout created for ${scopeLabel}. Note: ${result.data.conflicts.length} existing booking(s) in range.`);
      } else {
        toast.success(`Blackout period added for ${scopeLabel}`);
      }
      setBlackoutData({ start_date: "", end_date: "", reason: "", facilities: ["karaoke", "bbq"] });
      setShowBlackoutForm(false);
      setBlackoutConflicts([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add blackout");
    }
  };

  // Toggle facility in blackout selection
  const toggleBlackoutFacility = (facility) => {
    const current = blackoutData.facilities || [];
    if (current.includes(facility)) {
      // Don't allow deselecting if it's the only one selected
      if (current.length > 1) {
        setBlackoutData({ ...blackoutData, facilities: current.filter(f => f !== facility) });
      }
    } else {
      setBlackoutData({ ...blackoutData, facilities: [...current, facility] });
    }
  };

  const checkBlackoutConflicts = async () => {
    if (!blackoutData.start_date || !blackoutData.end_date) return;
    try {
      const res = await axios.post(`${API}/admin/blackout-dates/check-conflicts`, {
        start_date: blackoutData.start_date,
        end_date: blackoutData.end_date
      });
      setBlackoutConflicts(res.data.conflicts || []);
    } catch (error) {
      console.error("Error checking conflicts:", error);
    }
  };

  const handleFetchHolidaysFromApi = async () => {
    setFetchingFromApi(true);
    try {
      const result = await axios.post(`${API}/admin/public-holidays/fetch-from-api?year=${fetchApiYear}`);
      toast.success(`Imported ${result.data.imported_count} holidays for ${fetchApiYear}`);
      setShowFetchApiForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch holidays");
    } finally {
      setFetchingFromApi(false);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/members`, memberData);
      const roles = [];
      if (memberData.is_admin) roles.push('Admin');
      if (memberData.is_kj) roles.push(memberData.kj_allowance_eligible ? 'External KJ' : 'KJ (RN Member)');
      toast.success(`${roles.join(' + ') || 'Member'} account created`);
      setMemberData({
        name: "", email: "", phone: "", mailing_address: "",
        password: "", privacy_consent: true, is_admin: false, is_kj: true, is_rn_member: false, kj_allowance_eligible: true
      });
      setShowMemberForm(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    }
  };

  const handleSpecialBooking = async (e) => {
    e.preventDefault();
    try {
      const result = await axios.post(`${API}/admin/special-booking`, specialBookingData);
      toast.success(`Special booking created: ${result.data.booking.booking_code}`);
      setSpecialBookingData({
        member_name: "", member_email: "", member_phone: "",
        date: "", slot_type: "afternoon", discount_type: "none",
        kj_required: false, kj_name: "", discount_note: ""
      });
      setShowSpecialBookingForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    }
  };

  // BBQ Handlers
  const handleVerifyBbqPayment = async (bookingId) => {
    // Show the onePA dialog for BBQ payments
    setPendingVerification({ type: 'bbq', bookingId });
    setOnePATransactionNumber("");
    setShowOnePADialog(true);
  };

  const handleCancelBbqBooking = async (bookingId, bookingCode) => {
    if (!window.confirm(`Cancel BBQ booking ${bookingCode}?\n\nSTRICTLY NO REFUND as per policy.\nThis cannot be undone.`)) return;
    try {
      await axios.put(`${API}/admin/bbq/bookings/${bookingId}/cancel`);
      toast.success("BBQ booking cancelled. No refund (as per policy). Notification sent.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    }
  };

  const handleChangeBbqDate = async (booking) => {
    const newDate = prompt(
      `Change date for BBQ ${booking.booking_code}\n\nCurrent: ${booking.date} (${booking.slot_type})\nPit: ${booking.pit_selection === 'both' ? 'Both Pits' : 'Pit ' + booking.pit_selection?.slice(-1)}\nStatus: ${booking.status}\n\nEnter new date (YYYY-MM-DD):`,
      booking.date
    );
    if (!newDate) return;
    
    const newSlot = prompt(
      `Select time slot for ${newDate}:\n\nEnter: afternoon OR evening`,
      booking.slot_type
    );
    if (!newSlot || !['afternoon', 'evening'].includes(newSlot)) {
      toast.error("Invalid slot type. Must be 'afternoon' or 'evening'");
      return;
    }
    
    const statusNote = booking.status === 'completed_unpaid' 
      ? '\n\nNote: This completed-unpaid booking will be reset to PENDING status.'
      : '';
    
    const confirmed = window.confirm(
      `Change BBQ booking ${booking.booking_code} date?\n\n` +
      `FROM: ${booking.date} (${booking.slot_type})\n` +
      `TO: ${newDate} (${newSlot})\n` +
      `${statusNote}\n\n` +
      `Resident will be notified via email.`
    );
    if (!confirmed) return;
    
    try {
      await axios.put(`${API}/admin/bbq/bookings/${booking.booking_id}/change-date?new_date=${newDate}&new_slot_type=${newSlot}`);
      toast.success(`BBQ booking date changed to ${newDate} (${newSlot})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change BBQ date");
    }
  };


  // Fetch Annual Summary
  const fetchAnnualSummary = async () => {
    setLoadingAnnual(true);
    try {
      const res = await axios.get(`${API}/admin/finance/annual-summary?year=${annualYear}`);
      setAnnualSummary(res.data);
    } catch (error) {
      toast.error("Failed to fetch annual summary");
    } finally {
      setLoadingAnnual(false);
    }
  };

  // Fetch Centralised Report
  const fetchCentralisedReport = async () => {
    setLoadingReport(true);
    try {
      const res = await axios.get(`${API}/admin/finance/facility-report?year=${reportYear}&month=${reportMonth}`);
      setCentralisedReport(res.data);
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoadingReport(false);
    }
  };

  // Fetch KJ Allowance Report
  const fetchKjAllowanceReport = async () => {
    setLoadingKjReport(true);
    try {
      const res = await axios.get(`${API}/admin/finance/kj-allowance-report?year=${kjReportYear}&month=${kjReportMonth}`);
      setKjReport(res.data);
    } catch (error) {
      toast.error("Failed to fetch KJ allowance report");
    } finally {
      setLoadingKjReport(false);
    }
  };

  // Toggle KJ Eligibility
  const toggleKjEligibility = async (memberId, memberName, currentStatus) => {
    const newStatus = !currentStatus;
    const newRole = newStatus ? 'External KJ' : 'RN Member';
    const confirmMsg = newStatus 
      ? `Change ${memberName} to EXTERNAL KJ?\n\n• $35 Allowance per completed session`
      : `Change ${memberName} to RN MEMBER?\n\n• $0 Allowance (No payment)`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.put(`${API}/admin/members/${memberId}/kj-eligibility?eligible=${newStatus}`);
      toast.success(`${memberName} is now ${newRole}`);
      fetchMembers();
      fetchData(); // Refresh KJ list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update KJ role");
    }
  };

  // Trigger Unpaid Alert Email
  const triggerUnpaidAlert = async () => {
    try {
      await axios.post(`${API}/admin/bbq/trigger-unpaid-alert`);
      toast.success("Unpaid alert email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send alert");
    }
  };

  // Generate PDF for Centralised Report
  const handleGenerateCentralisedPDF = () => {
    try {
      generateCentralisedPDF(centralisedReport, reportMonth, reportYear);
      toast.success("PDF report downloaded");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  // Generate PDF for KJ Allowance Report
  const handleGenerateKjAllowancePDF = (filteredEntries = null, payFilterLabel = 'All', kjFilterName = '') => {
    try {
      generateKjAllowancePDF(kjReport, kjReportMonth, kjReportYear, kjFilterName, filteredEntries, payFilterLabel);
      toast.success("KJ Allowance Report PDF downloaded");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  // Filter functions
  const filteredBookings = confirmedBookings.filter(b =>
    b.booking_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.member_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.phone?.includes(searchQuery) ||
    b.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    m.email?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
    m.phone?.includes(memberSearchQuery)
  );

  // BBQ Filter
  const filteredBbqBookings = bbqBookings.filter(b =>
    b.booking_code?.toLowerCase().includes(bbqSearchQuery.toLowerCase()) ||
    b.name?.toLowerCase().includes(bbqSearchQuery.toLowerCase()) ||
    b.mobile?.includes(bbqSearchQuery) ||
    b.block_unit?.toLowerCase().includes(bbqSearchQuery.toLowerCase()) ||
    b.email?.toLowerCase().includes(bbqSearchQuery.toLowerCase())
  );

  // Scroll tabs
  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: direction * 120, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-bold animate-pulse">Loading Admin Panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-white/20 rounded"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-syne font-bold notranslate" translate="no">YCK Court RN</h1>
                <p className="text-xs text-white/80 hidden sm:block">Admin Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="dark" />
              <span className="text-xs sm:text-sm bg-white/20 px-2 py-1 rounded hidden sm:block notranslate" translate="no">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="text-xs px-3 py-1 bg-white text-primary font-bold border-2 border-black hover:bg-gray-100"
              >
                LOGOUT
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b-2 border-gray-200 sticky top-[60px] z-40">
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center">
            {/* Mobile scroll buttons */}
            <button
              onClick={() => scrollTabs(-1)}
              className="md:hidden p-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <ChevronLeft size={20} />
            </button>
            
            {/* Tabs container - horizontal scroll on mobile */}
            <div
              ref={tabsRef}
              className="flex-1 flex overflow-x-auto scrollbar-hide gap-1 py-2"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setActiveSubView(null); }}
                    className={`flex items-center gap-2 px-4 py-3 font-bold text-sm whitespace-nowrap transition-all flex-shrink-0
                      ${isActive 
                        ? 'text-primary border-b-4 border-primary bg-primary/5' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-b-4 border-transparent'
                      }`}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                    {tab.id === 'karaoke' && pendingBookings.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                        {pendingBookings.length}
                      </span>
                    )}
                    {tab.id === 'bbq' && bbqPendingBookings.length > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                        {bbqPendingBookings.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Mobile scroll buttons */}
            <button
              onClick={() => scrollTabs(1)}
              className="md:hidden p-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* KARAOKE TAB */}
        {activeTab === 'karaoke' && (
          <AdminKaraokeTab
            activeSubView={activeSubView}
            setActiveSubView={setActiveSubView}
            pendingBookings={pendingBookings}
            bookings={bookings}
            confirmedBookings={confirmedBookings}
            filteredBookings={filteredBookings}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showSpecialBookingForm={showSpecialBookingForm}
            setShowSpecialBookingForm={setShowSpecialBookingForm}
            bookingsPagination={bookingsPagination}
            bookingsLoading={bookingsLoading}
            onVerifyPayment={handleVerifyPayment}
            onCancelPendingBooking={handleCancelPendingBooking}
            onCancelPaidBooking={handleCancelPaidBooking}
            onChangeDate={handleChangeBookingDate}
            onAssignKJ={(booking) => { setBookingToAssignKJ(booking); setShowAssignKJDialog(true); }}
            onApproveSwap={handleApproveSwap}
            onRejectSwap={handleRejectSwap}
            canCancelBooking={canCancelBooking}
            onPageChange={handleBookingsPageChange}
            onSearchChange={handleBookingsSearchChange}
            onKjFilterChange={handleBookingsKjFilterChange}
            onStatusFilterChange={handleBookingsStatusFilterChange}
            onSortChange={handleBookingsSortChange}
            onFetchData={fetchData}
            onAutoCompletePast={handleAutoCompletePast}
          />
        )}

        {activeTab === 'calendar' && (
          <AdminCalendarTab
            holidays={holidays}
            blackoutDates={blackoutDates}
            bookings={bookings}
            bbqBookings={bbqBookings}
            showHolidayForm={showHolidayForm}
            setShowHolidayForm={setShowHolidayForm}
            holidayData={holidayData}
            setHolidayData={setHolidayData}
            showFetchApiForm={showFetchApiForm}
            setShowFetchApiForm={setShowFetchApiForm}
            fetchApiYear={fetchApiYear}
            setFetchApiYear={setFetchApiYear}
            fetchingFromApi={fetchingFromApi}
            showBlackoutForm={showBlackoutForm}
            setShowBlackoutForm={setShowBlackoutForm}
            blackoutData={blackoutData}
            setBlackoutData={setBlackoutData}
            blackoutConflicts={blackoutConflicts}
            setBlackoutConflicts={setBlackoutConflicts}
            onAddHoliday={handleAddHoliday}
            onDeleteHoliday={handleDeleteHoliday}
            onAddBlackout={handleAddBlackout}
            onDeleteBlackout={handleDeleteBlackout}
            onFetchHolidaysFromApi={handleFetchHolidaysFromApi}
            onToggleBlackoutFacility={toggleBlackoutFacility}
          />
        )}

        {activeTab === 'bbq' && (
          <AdminBBQTab
            activeSubView={activeSubView}
            setActiveSubView={setActiveSubView}
            bbqBookings={bbqBookings}
            bbqPendingBookings={bbqPendingBookings}
            onVerifyBbqPayment={handleVerifyBbqPayment}
            onCancelBbqBooking={handleCancelBbqBooking}
            onChangeBbqDate={handleChangeBbqDate}
            onTriggerUnpaidAlert={triggerUnpaidAlert}
          />
        )}

        {activeTab === 'directory' && (
          <AdminDirectoryTab
            activeSubView={activeSubView}
            setActiveSubView={setActiveSubView}
            members={members}
            filteredMembers={filteredMembers}
            memberSearchQuery={memberSearchQuery}
            setMemberSearchQuery={setMemberSearchQuery}
            showMemberForm={showMemberForm}
            setShowMemberForm={setShowMemberForm}
            memberData={memberData}
            setMemberData={setMemberData}
            user={user}
            onCreateMember={handleCreateMember}
            onEditMember={handleEditMember}
            onDeleteMember={handleDeleteMember}
            onToggleKjEligibility={toggleKjEligibility}
            onFetchMembers={fetchMembers}
          />
        )}

        {activeTab === 'finance' && (
          <AdminFinanceTab
            annualYear={annualYear}
            setAnnualYear={setAnnualYear}
            annualSummary={annualSummary}
            loadingAnnual={loadingAnnual}
            onFetchAnnualSummary={fetchAnnualSummary}
            reportMonth={reportMonth}
            setReportMonth={setReportMonth}
            reportYear={reportYear}
            setReportYear={setReportYear}
            centralisedReport={centralisedReport}
            loadingReport={loadingReport}
            onFetchCentralisedReport={fetchCentralisedReport}
            onGenerateCentralisedPDF={handleGenerateCentralisedPDF}
            confirmedBookings={confirmedBookings}
            pendingBookings={pendingBookings}
            bookings={bookings}
          />
        )}

        {activeTab === 'kjops' && (
          <AdminKJOpsTab
            activeSubView={activeSubView}
            setActiveSubView={setActiveSubView}
            kjDetails={kjDetails}
            confirmedBookings={confirmedBookings}
            kjReportMonth={kjReportMonth}
            setKjReportMonth={setKjReportMonth}
            kjReportYear={kjReportYear}
            setKjReportYear={setKjReportYear}
            kjReport={kjReport}
            loadingKjReport={loadingKjReport}
            onFetchKjAllowanceReport={fetchKjAllowanceReport}
            onGenerateKjAllowancePDF={handleGenerateKjAllowancePDF}
          />
        )}


      </main>

      {/* onePA Transaction Verification Dialog */}
      <OnePADialog
        isOpen={showOnePADialog}
        onClose={() => {
          setShowOnePADialog(false);
          setOnePATransactionNumber("");
          setPendingVerification(null);
        }}
        transactionNumber={onePATransactionNumber}
        setTransactionNumber={setOnePATransactionNumber}
        onConfirm={confirmVerifyPayment}
        bookingType={pendingVerification?.type || 'karaoke'}
      />
      
      {/* KJ Assignment Dialog */}
      <AssignKJDialog
        booking={bookingToAssignKJ}
        isOpen={showAssignKJDialog}
        onClose={() => {
          setShowAssignKJDialog(false);
          setBookingToAssignKJ(null);
        }}
        onSuccess={handleAssignKJSuccess}
      />

      {/* Edit Member Modal */}
      <EditMemberModal
        isOpen={showEditMemberModal}
        member={editingMember}
        formData={editMemberData}
        setFormData={setEditMemberData}
        onSubmit={handleUpdateMember}
        onClose={() => {
          setShowEditMemberModal(false);
          setEditingMember(null);
        }}
      />
    </div>
  );
};

export default AdminPage;
